import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { ShieldCheck, ExternalLink, Check, ArrowRight } from "lucide-react";

const STEPS = [
  { t: "Confirm you may be eligible", d: "Veterans, active-duty service members, National Guard/Reserve members, and certain surviving spouses may be eligible. Eligibility rules and required documentation are set by VA." },
  { t: "Gather your service documentation", d: "Common items include DD-214 (for veterans), a statement of service (active duty), or NGB-22 (National Guard). Surviving spouses may need marriage/death certificates." },
  { t: "Request your Certificate of Eligibility (COE)", d: "Most buyers request the COE through their VA-approved lender via the WebLGY system. You can also request it yourself through eBenefits / VA.gov, or by mail." },
  { t: "Review your COE with your lender", d: "Your COE shows your entitlement amount and any prior-use notations. Your lender uses this alongside credit, income, and property details for a loan decision." },
];

const LINKS = [
  { label: "VA — How to request a COE (official)", href: "https://www.va.gov/housing-assistance/home-loans/how-to-request-coe/" },
  { label: "VA — Home Loan program overview", href: "https://www.benefits.va.gov/HOMELOANS/" },
];

export default function COE() {
  return (
    <>
      <Nav />
      <main className="container-hs py-10 md:py-14 max-w-3xl">
        <span className="badge-soft" data-testid="coe-tag">VA Home Loan Checklist</span>
        <h1 className="font-display text-3xl sm:text-4xl text-brand-ink mt-3">Certificate of Eligibility (COE)</h1>
        <p className="text-brand-muted mt-3 leading-relaxed">
          A VA Certificate of Eligibility (COE) is what shows your lender that you may be eligible for a VA home loan.
          It doesn't guarantee approval, but you'll typically need it as part of your VA loan application.
        </p>

        <div className="card-hs p-6 mt-6">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="text-brand-accent" size={20} />
            <h2 className="font-display text-xl text-brand-ink">Quick checklist</h2>
          </div>
          <ol className="space-y-4">
            {STEPS.map((s, i) => (
              <li key={s.t} data-testid={`coe-step-${i}`} className="flex gap-3">
                <span className="flex-shrink-0 mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-soft text-brand-accent text-xs font-semibold">{i + 1}</span>
                <div>
                  <div className="font-medium text-brand-ink">{s.t}</div>
                  <div className="text-sm text-brand-muted mt-0.5 leading-relaxed">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="card-hs p-6 mt-6">
          <h2 className="font-display text-lg text-brand-ink mb-3">Helpful resources</h2>
          <ul className="space-y-2">
            {LINKS.map(l => (
              <li key={l.href}>
                <a data-testid={`coe-link-${l.href}`} href={l.href} target="_blank" rel="noopener noreferrer"
                  className="text-brand-accent hover:underline inline-flex items-center gap-1 text-sm">
                  {l.label} <ExternalLink size={12} />
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-brand-border bg-brand-soft/40 p-5 mt-6 text-xs text-brand-muted leading-relaxed">
          <b className="text-brand-ink">Note:</b> Home Cost Path is not affiliated with or endorsed by the U.S. Department of Veterans Affairs.
          COE requirements, eligibility, and processes are set by VA and may change. Confirm current requirements with
          VA and your lender. This checklist is informational only and does not affect calculator results.
        </div>

        <div className="flex justify-center mt-8">
          <Link to="/analyze" className="btn-primary">Back to my analysis <ArrowRight size={16} /></Link>
        </div>
      </main>
      <Footer />
    </>
  );
}
